import {
  Button,
  ButtonGroup,
  ModalIcon,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useProStudioViewerRole } from '../../hooks';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
} from '../../xstate/app.xstate';
import { useDeleteSolutionModal } from '../modals/delete-solution-modal/use-delete-solution-modal';
import { useGenericModal } from '../modals/generic-modal/use-generic-modal';
import { GenericModal } from '../modals/generic-modal/generic-modal';
import {
  deleteDialogClosedEffect,
  deleteFromFailureDialog,
  failureDialogCloseEffect,
  failureDialogPlanEffect,
} from '../../utils/solution-open-failure';

/**
 * Failed-open dialog. `Root` in `router.tsx` mounts this once, beside the
 * outlet, so it stays on All Solutions and on the solution pages. The page
 * wrapper must not own it: an open `ps-modal` is moved into a portal, and
 * unmounting that wrapper throws `removeChild` on the moved node.
 *
 * A user close sends `dismissSolutionOpenFailure` and leaves
 * `solutionOpenFailure` set, so the solutions table does not call
 * `getBotById` again. A close this component requested itself
 * (`programmaticClose`) does not send that event: `ps-modal` emits `close`
 * after its transition, and that emit can land after a newer failure.
 *
 * Delete uses its own `ps-modal` (`solution-open-failure-delete-modal`).
 * This dialog closes first, and the delete dialog opens from that `close`
 * event, so the two are not open together. Cancel opens this dialog again.
 *
 * The footer is a child on the first render. `ps-modal` records slots only
 * in `BaseElement.update` while `hasUpdated` is still false
 * (`base-element.ts`), and `ModalWC` emits `<slot name="footer">` only when
 * that record already contains `footer`. This dialog mounts before a failure
 * exists, so a footer added with the failure text is never projected.
 */
export const SolutionOpenFailureModal = () => {
  const openFailure = useProStudioCtxSelector(
    (ctx) => ctx.solutionOpenFailure ?? null
  );
  const dismissed = useProStudioCtxSelector(
    (ctx) => ctx.solutionOpenFailureDismissed === true
  );
  const isDeleting = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.deletingBotFlow'
  );
  const isViewer = useProStudioViewerRole();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { modalRef, openModal, closeModal } = useGenericModal();
  const failureRef = useRef(openFailure);
  const dismissedRef = useRef(dismissed);
  const deletingRef = useRef(isDeleting);
  const programmaticClose = useRef(false);
  const deleteHandoff = useRef(false);
  failureRef.current = openFailure;
  dismissedRef.current = dismissed;
  deletingRef.current = isDeleting;

  const { openModal: openDeleteModal, deleteSolutionModal } =
    useDeleteSolutionModal({
      preferOpenFailure: true,
      testId: 'solution-open-failure-delete-modal',
      onClosed: () => {
        const effect = deleteDialogClosedEffect({
          handoff: deleteHandoff.current,
          deleting: deletingRef.current,
          dismissed: dismissedRef.current,
          failureHeld: !!failureRef.current,
        });

        if (effect.clearHandoff) {
          deleteHandoff.current = false;
        }

        if (effect.reopenFailure) {
          openModal();
        }
      },
    });
  const shownFailure = useRef(openFailure);
  if (openFailure) {
    shownFailure.current = openFailure;
  }
  const failure = openFailure ?? shownFailure.current;

  useEffect(() => {
    const step = failureDialogPlanEffect({
      openFailureId: openFailure?.solutionId,
      dismissed,
      modalOpen: !!modalRef.current?.open,
    });

    if (step === 'none') {
      return;
    }

    if (step === 'close' || step === 'close-marked') {
      if (step === 'close-marked') {
        programmaticClose.current = true;
      }

      closeModal();
      return;
    }

    openModal();
  }, [openFailure, dismissed, openModal, closeModal, modalRef]);

  const onClose = () => {
    const effect = failureDialogCloseEffect({
      programmatic: programmaticClose.current,
      deleteHandoff: deleteHandoff.current,
    });

    if (effect.clearProgrammatic) {
      programmaticClose.current = false;
    }

    if (effect.dismiss) {
      sendProStudioEvent({ type: 'dismissSolutionOpenFailure' });
      return;
    }

    if (effect.openDelete) {
      openDeleteModal();
    }
  };

  const retry = () => {
    if (!openFailure) {
      return;
    }

    sendProStudioEvent({
      type: 'selectBot',
      id: openFailure.solutionId,
      name: openFailure.displayName,
      force: true,
      cb: (path?: string) => navigate(path || ''),
    });
  };

  const requestDelete = () => {
    deleteHandoff.current = true;

    if (deleteFromFailureDialog(!!modalRef.current?.open) === 'close-first') {
      programmaticClose.current = true;
      closeModal();
      return;
    }

    openDeleteModal();
  };

  return (
    <>
      <GenericModal
        test-id="solution-open-failure-modal"
        modalRef={modalRef}
        onClose={onClose}
      >
        <ButtonGroup slot="footer">
          <Button
            test-id="solution-open-failure-retry"
            width="full"
            size="large"
            variant="secondary"
            onClick={retry}
          >
            {t('pro-studio/homepage:open_failure.retry')}
          </Button>
          {!isViewer ? (
            <Button
              test-id="solution-open-failure-delete"
              width="full"
              size="large"
              variant="warning"
              onClick={requestDelete}
            >
              {t('pro-studio/homepage:open_failure.delete')}
            </Button>
          ) : null}
        </ButtonGroup>
        {failure ? (
          <>
            <Stack justifyContent="center">
              <ModalIcon name="error" />
            </Stack>
            <TextTitle size="small" textAlign="center">
              {t('pro-studio/homepage:open_failure.title', {
                name: failure.displayName,
              })}
            </TextTitle>
            <Spacer size="small" />
            <TextBody variant="secondary" textAlign="center">
              {t(
                isViewer
                  ? 'pro-studio/homepage:open_failure.body_viewer'
                  : 'pro-studio/homepage:open_failure.body'
              )}
            </TextBody>
          </>
        ) : null}
      </GenericModal>
      {deleteSolutionModal}
    </>
  );
};
